import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Accordion, Modal, Row, Col, Form, Card } from 'react-bootstrap-5';

import Button from 'shared/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders, faTimes } from '@fortawesome/pro-solid-svg-icons';

import FilterNumberPair from '../../search_sidebar/filter_number_pair';
import FilterDatePickerPair from '../../search_sidebar/filter_datepicker_pair';
import SidebarSearch from '../../search_sidebar/sidebar_search';
import { capitalize, isEqual } from 'lodash';

const filterBoxStyles = {
  maxHeight: '300px',
  overflowY: 'auto'
};

export default function AllFiltersModal({
  allFilterOrder,
  filterMapping,
  startingFilters,
  show,
  onChangeFilters,
  onClearFilters,
  onCloseModal
}) {
  const FILTER_TYPES_REQUIRING_OPTIONS = ['multiselect', 'accordion', 'dropdown'];
  const [filters, setFilters] = useState(startingFilters);

  const filtersModified = useMemo(() => {
    return !isEqual(filters, startingFilters);
  }, [filters, startingFilters]);

  useEffect(() => {
    setFilters(startingFilters);
  }, [show]);

  function changeRangeFilter(e, minOrMax, filterKey) {
    setFilters({ ...filters, [filterKey]: { ...filters[filterKey], [minOrMax]: e.currentTarget.value } });
  }

  function changeDateFilter(date, minOrMax, filterKey) {
    setFilters({ ...filters, [filterKey]: { ...filters[filterKey], [minOrMax]: date } });
  }

  function changeMultiSelectFilters(e, filterKey, optionId) {
    const { checked } = e.target;

    if (checked) {
      setFilters({ ...filters, [filterKey]: [...filters[filterKey], optionId] });
    } else {
      setFilters({ ...filters, [filterKey]: filters[filterKey].filter(id => id !== optionId) });
    }
  }

  function changeSearchFilter(e, filterKey) {
    setFilters({ ...filters, [filterKey]: e.currentTarget.value });
  }

  function clearFilters() {
    onClearFilters();
    onCloseModal();
  }

  function saveFilters() {
    onChangeFilters(filters);
    onCloseModal();
  }

  function renderDateRangeBox(filterKey, category) {
    return (
      <FilterDatePickerPair
        filter={filters[filterKey]}
        maxPlaceholder={category.maxPlaceholder}
        minPlaceholder={category.minPlaceholder}
        name={`${filterKey}InputGroup`}
        newDesignSystem
        valueKeys={['min', 'max']}
        withIcon={category.icon}
        onChange={(date, minOrMax) => changeDateFilter(date, minOrMax, filterKey)}
      />
    );
  }

  function renderRangeBox(filterKey, category) {
    return (
      <FilterNumberPair
        filter={filters[filterKey]}
        maxPlaceholder={category.maxPlaceholder}
        minPlaceholder={category.minPlaceholder}
        name={`${filterKey}InputGroup`}
        newDesignSystem
        // FIXME: valueKeys should be [0, <integer max>] - we don't have a specific limit in the backend
        valueKeys={['min', 'max']}
        withIcon={category.icon}
        onChange={(event, minOrMax) => changeRangeFilter(event, minOrMax, filterKey)}
      />
    );
  }

  function renderFilterBox(filterKey) {
    const { options } = filterMapping[filterKey];

    return (
      <Card style={filterBoxStyles}>
        <Card.Body>
          <fieldset>
            {options.map(option => (
              <Form.Check
                aria-label={option.label}
                checked={filters[filterKey].includes(option.value)}
                id={`${filterKey}-${option.value}`}
                key={option.value}
                label={option.label}
                name={`${option.label}_check`}
                onChange={e => changeMultiSelectFilters(e, filterKey, option.value)}
              />
            ))}
          </fieldset>
        </Card.Body>
      </Card>
    );
  }

  const renderDropdownFilterBox = (filterKey) => {
    const { options } = filterMapping[filterKey];
    const normalizedOptions = options.map(opt => ({
      ...opt,
      value: String(opt.value)
    }));
    const dropdownTitle = `All ${capitalize(filterKey)}s`;

    return (
      <Card className="border-top-0 border-bottom-0 overflow-hidden filter-accordion-container">
        <Accordion alwaysOpen className="filter-accordion">
          <Accordion.Item
            className="filter-item"
            eventKey="0"
            key="0"
          >
            <Accordion.Header>
              {dropdownTitle}
            </Accordion.Header>
            <Accordion.Body className="p-3">
              {normalizedOptions.map((option) => {
                return (
                  <fieldset key={option.value}>
                    <Form.Check
                      checked={filters[filterKey].includes(option.value)}
                      id={`${filterKey}-${option.value}`}
                      key={option.value}
                      label={option.label}
                      name={`${option.label}_check`}
                      onChange={(e) => {
                        const { checked } = e.target;
                        if (checked) {
                          setFilters({
                            ...filters,
                            [filterKey]: [...filters[filterKey], option.value]
                          });
                        } else {
                          setFilters({
                            ...filters,
                            [filterKey]: filters[filterKey].filter(value => value !== option.value)
                          });
                        }
                      }}
                    />
                  </fieldset>
                );
              })}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Card>
    );
  };

  function renderChildFilter(key) {
    const filterData = filterMapping[key];
    const childFilterKey = filterData.childFilter.key;

    return (
      <div className="mb-4">
        <Form.Check
          checked={!!filters[childFilterKey]}
          id={`${key}-${childFilterKey}`}
          label={
            <span className="text-muted">
              {filterData.childFilter.label}
            </span>
          }
          name={`${childFilterKey}_check`}
          style={{ position: 'relative', bottom: '2px' }}
          onChange={() => setFilters({ ...filters, [childFilterKey]: !filters[childFilterKey] })}
        />
      </div>
    );
  }

  const renderSearchFilter = (filterKey) => {
    return (
      <React.Fragment>
        <SidebarSearch
          className="filter-search-container"
          filter={filters[filterKey]}
          name={`${filterKey}InputGroup`}
          newDesignSystem
          placeholder="Search by name, address, city, or zip"
          value={filters[filterKey]}
          onChange={e => changeSearchFilter(e, filterKey)}
        />
      </React.Fragment>
    );
  };

  const renderAccordionFilterBox = (filterKey, category) => {
    const { options } = filterMapping[filterKey];
    const childFilterKey = filterMapping[filterKey].childLabel;
    let childOptions;

    if (childFilterKey) {
      childOptions = filterMapping[childFilterKey].options;
    } else {
      childOptions = [];
    }

    return (
      <Card className="border-top-0 border-bottom-0 overflow-hidden filter-accordion-container">
        <Accordion alwaysOpen className="filter-accordion">
          {options.map((option, index) => {
            const childOption = childOptions[index];

            return (
              <Accordion.Item
                className="filter-item"
                eventKey={index}
                key={index}
              >
                <Accordion.Header>
                  <div className="category-label">
                    {
                      childOption &&
                      <Form.Check
                        checked={filters[filterKey].includes(option.value) ||
                          childOption.every(subclass => filters[childFilterKey].includes(subclass.value))}
                        id={`${filterKey}-${open.value}`}
                        key={index}
                        label={
                          <span className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={category.accordionIcons[index].icon}/>
                            {option.label}
                          </span>
                        }
                        name={`${option.label}_check`}
                        onChange={(e) => {
                          const subclassValues = childOption.map(subclass => subclass.value);

                          if (e.target.checked) {
                            // Add all subclass values to the child filter
                            setFilters({
                              ...filters,
                              [filterKey]: [...filters[filterKey], option.value],
                              [childFilterKey]: [...filters[childFilterKey], ...subclassValues]
                            });
                          } else {
                            // Remove all subclass values from child filter
                            setFilters({
                              ...filters,
                              [filterKey]: filters[filterKey].filter(value => value !== option.value),
                              [childFilterKey]: filters[childFilterKey].filter(value => !subclassValues.includes(value))
                            });
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    }
                    {!childOption &&
                      <Form.Check
                        checked={filters[filterKey].includes(option.value)}
                        id={`${filterKey}-${open.value}`}
                        key={index}
                        label={
                          <span className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={category.accordionIcons[index].icon}/>
                            {option.label}
                          </span>
                        }
                        name={`${option.label}_check`}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters({
                              ...filters,
                              [filterKey]: [...filters[filterKey], option.value]
                            });
                          } else {
                            setFilters({
                              ...filters,
                              [filterKey]: filters[filterKey].filter(value => value !== option.value)
                            });
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    }
                  </div>
                </Accordion.Header>
                {
                  childOption &&
                  <Accordion.Body className="p-3">
                    {childOption.map((subclass) => {
                      return (
                        <fieldset key={subclass.value}>
                          <Form.Check
                            checked={filters[childFilterKey].includes(subclass.value)}
                            id={`${filterKey}-${subclass.value}`}
                            key={subclass.value}
                            label={subclass.label}
                            name={`${subclass.label}_check`}
                            onChange={(e) => {
                              const { checked } = e.target;
                              if (checked) {
                                setFilters({
                                  ...filters,
                                  [childFilterKey]: [...filters[childFilterKey], subclass.value]
                                });
                              } else {
                                setFilters({
                                  ...filters,
                                  [childFilterKey]: filters[childFilterKey].filter(value => value !== subclass.value)
                                });
                              }
                            }}
                          />
                        </fieldset>
                      );
                    })}
                  </Accordion.Body>
                }
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Card>
    );
  };

  const validFilters = Object.keys(filterMapping).filter((filterKey) => {
    const filterType = filterMapping[filterKey].type;

    if (FILTER_TYPES_REQUIRING_OPTIONS.includes(filterType)) {
      return filterMapping[filterKey].options.length > 0;
    }

    return true;
  });

  const filterSection = (filterCategories) => {
    return (
      <div className="d-flex flex-column gap-6">
        {filterCategories.map((category) => {
          const filters = validFilters.filter(key => filterMapping[key].category === category.label);

          return (
            <div key={category.label}>
              {category.label !== 'Search' && <h6 className="mb-2 fw-bold">{category.label}</h6>}
              <div className="d-flex flex-column gap-4">
                {filters.map(key => (
                  <div key={key}>
                    {filterMapping[key].showLabel !== false && (
                      <div className="form-group">
                        <div className="form-label mb-1">{filterMapping[key].label}</div>
                        {filterMapping[key].childFilter ? renderChildFilter(key) : null}
                      </div>
                    )}

                    {filterMapping[key].type === 'search' && renderSearchFilter(key)}
                    {filterMapping[key].type === 'multiselect' && renderFilterBox(key)}
                    {filterMapping[key].type === 'range' && renderRangeBox(key, category)}
                    {filterMapping[key].type === 'date' && renderDateRangeBox(key, category)}
                    {filterMapping[key].type === 'dropdown' && renderDropdownFilterBox(key)}
                    {filterMapping[key].type === 'accordion' && renderAccordionFilterBox(key, category)}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const mainFilters = () => {
    return (
      allFilterOrder.map((categories, index) => {
        return (
          <Col className={`py-4 ${index === 0 ? 'bg-light' : ''}`} key={index} md={6} xl={4}>
            {filterSection(categories)}
          </Col>
        );
      })
    );
  };

  return (
    <Modal fullscreen="lg-down" show={show} size="xl" onHide={onCloseModal}>
      <Modal.Header className="border-bottom" closeButton>
        <Modal.Title>
          <FontAwesomeIcon className="me-2 text-primary" icon={faSliders}/>
          All Filters
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="py-0">
        <Row className="filter-body-container">
          {mainFilters()}
        </Row>
      </Modal.Body>
      <Modal.Footer className="border-top">
        <Row className="w-100">
          <Col>
            <Button.Danger className="ds-button--plain gap-2" name="clearFilters" onClick={clearFilters}>
              <FontAwesomeIcon icon={faTimes}/> Clear All
            </Button.Danger>
          </Col>
          <Col className="d-flex gap-2"  xs="auto">
            <Button.Cancel name="cancel" onClick={onCloseModal}>
              Cancel
            </Button.Cancel>
            <Button.Primary
              disabled={!filtersModified}
              name="saveFilters"
              onClick={saveFilters}
            >
              Apply Filters
            </Button.Primary>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
}

AllFiltersModal.propTypes = {
  allFilterOrder: PropTypes.array.isRequired,
  filterMapping: PropTypes.object.isRequired,
  show: PropTypes.bool.isRequired,
  startingFilters: PropTypes.object.isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func.isRequired,
};
